import {
  Alert,
  Card,
  CardBody,
  Form,
  FormField,
  Heading,
  Inline,
  Paragraph,
  Separator,
  TextInput,
  ToggleBadge,
  ToggleGroup,
  Tooltip,
  useMatchMediaTierProp,
} from "@george-labs.com/design-system"
import {
  CustomerSoftFactAnswerParam,
  SetSoftFactsRq,
} from "@smartx/mikro-vt-client-api"
import { useFormik } from "formik"
import React, { useContext } from "react"
import { useTranslation } from "react-i18next"
import { useNavigate } from "react-router"
import * as Yup from "yup"
import { offerSetSoftFactsApi } from "../../api/apiClient"
import { useOfferData } from "../../app/hooks"
import { useOfferScreenFlow } from "../../hooks/useOfferScreenFlow"
import AuthContext from "../../providers/AuthProvider"
import { RequestHeader } from "../../utils/utils"
import { OfferNavButtons, OfferSaveButton } from "./OfferNavButtons"
import { useSaveModal } from "./OfferModalContext"

const OfferSoftFacts = () => {
  const authContext = useContext(AuthContext)
  const { t } = useTranslation()
  const navigate = useNavigate()
  const offerData = useOfferData()
  const { goToNextOfferScreen } = useOfferScreenFlow()
  const { openErrorModal } = useSaveModal()
  const makValidationMsg = t("offers.softFacts.warning.makRegistrationNumber")
  const answerValidationMsg = t("offers.softFacts.warning.facts")
  const showMakRegistrationNumber =
    offerData?.customer?.showMakRegistrationNumber
  const questions = offerData?.customerSoftFactQuestions || []

  const formik = useFormik({
    initialValues: {
      makRegistrationNumber: offerData?.customer?.makRegistrationNumber || "",
      questions: {
        ...questions.reduce(
          (acc, question) => {
            acc[question.questionCode] = question?.answer?.answerCode
            return acc
          },
          {} as Record<string, string>,
        ),
      },
    },
    validationSchema: Yup.object({
      makRegistrationNumber: showMakRegistrationNumber
        ? Yup.string()
            .required(makValidationMsg)
            .min(10, makValidationMsg)
            .max(10, makValidationMsg)
        : Yup.string().notRequired(),
      questions: Yup.object().test(
        "all-answered",
        answerValidationMsg,
        (value) => Object.values(value || {}).every((el) => !!el),
      ),
    }),
    onSubmit: () => handleSubmit(),
  })

  const saveButtonHandler = () => {
    save(false)
  }

  const handleSubmit = () => {
    save(true)
  }

  const getAnswersToSave = () => {
    const answersToSave: CustomerSoftFactAnswerParam[] = Object.keys(
      formik.values.questions,
    )?.map((questionCode) => {
      const selectedAnswerCode = formik.values.questions?.[questionCode]
      const answer = questions
        ?.find((el) => el.questionCode === questionCode)
        ?.answerConfigs?.find((a) => a.answerCode === selectedAnswerCode)

      return {
        questionCode,
        answer: answer
          ? {
              answerCode: answer.answerCode,
              answerText: answer.answerText,
              answerMode: "MANUAL",
            }
          : null,
      }
    })

    return answersToSave?.filter((el) => !!el.answer)
  }

  const save = async (nextStep: boolean) => {
    authContext.refresh()

    const rq: SetSoftFactsRq = {
      header: new RequestHeader(),
      caseId: offerData?.caseId,
      answerParams: getAnswersToSave(),
      makRegistrationNumber: showMakRegistrationNumber
        ? formik.values.makRegistrationNumber
        : null,
      nextStep,
    }

    try {
      const result = await offerSetSoftFactsApi.setSoftFacts(rq)
      if (!result?.data?.success) {
        throw new Error("Soft facts error")
      }

      if (nextStep) {
        goToNextOfferScreen(offerData?.caseId)
      } else {
        navigate("/dashboard", { replace: true })
      }
    } catch {
      openErrorModal(() => goToNextOfferScreen(offerData?.caseId))
    }
  }

  const headerContent = (
    <Heading level={1}>{t("offers.softFacts.title")}</Heading>
  )

  const textContent = (
    <Paragraph className="mb-3">{t("offers.softFacts.text")}</Paragraph>
  )

  const navButtonsContent = <OfferNavButtons />

  const saveButtonContent = (
    <OfferSaveButton onSaveButtonClicked={saveButtonHandler} />
  )

  const getSelectedValues = (questionCode: string) => {
    const values = formik.values.questions

    if (Object.hasOwn(values, questionCode) && values[questionCode]) {
      return [values[questionCode]]
    }

    return []
  }

  const formContent = (
    <>
      {showMakRegistrationNumber && (
        <FormField
          label={
            <>
              <Inline className="mr-1">
                {t("offers.softFacts.fields.makRegistrationNumber")}
              </Inline>
              <Tooltip
                content={t(
                  "offers.softFacts.fields.makRegistrationNumber.tooltip",
                )}
              />
            </>
          }
          input={
            <TextInput
              value={formik.values.makRegistrationNumber}
              onChange={formik.handleChange}
              name="makRegistrationNumber"
              autoComplete="off"
            />
          }
          labelPosition={FormField.LABEL_POSITION.ABOVE}
          optionalLabel={""}
        />
      )}

      {questions.map((fact, factIdx) => (
        <React.Fragment key={`fact-${fact.questionCode}`}>
          <Heading level={3}>{fact.question}</Heading>
          <ToggleGroup
            type={ToggleGroup.TYPE.RADIO}
            layout={ToggleGroup.LAYOUT.NONE}
            toggledItems={getSelectedValues(fact.questionCode)}
          >
            {fact.answerConfigs.map((answer, answerIdx) => (
              <ToggleBadge
                key={`fact-${fact.questionCode}-answer-${answerIdx}`}
                label={answer.answerText}
                name={`questions.question${factIdx}`}
                size={ToggleBadge.SIZE.SMALL}
                value={answer.answerCode}
                type={ToggleBadge.TYPE.RADIO}
                onChange={() =>
                  formik.setFieldValue(
                    `questions.${fact.questionCode}`,
                    answer.answerCode,
                  )
                }
              />
            ))}
          </ToggleGroup>
        </React.Fragment>
      ))}
    </>
  )

  const warning = (
    <>
      {!formik.isValid && !!formik.submitCount && (
        <>
          <Alert
            heading=""
            variant={Alert.VARIANT.WARNING}
            hidden={!formik.errors.makRegistrationNumber}
          >
            {formik.errors.makRegistrationNumber}
          </Alert>

          {typeof formik.errors.questions === "string" && (
            <Alert heading="" variant={Alert.VARIANT.WARNING}>
              {formik.errors.questions}
            </Alert>
          )}
        </>
      )}
    </>
  )

  const pageContent = useMatchMediaTierProp({
    xs: (
      <>
        {headerContent}

        {textContent}

        {formContent}

        {warning}

        {navButtonsContent}
      </>
    ),
    md: (
      <Card nested={false}>
        <CardBody>
          {headerContent}

          {textContent}

          {formContent}

          {warning}
        </CardBody>

        <Separator />

        <CardBody>{navButtonsContent}</CardBody>
      </Card>
    ),
  })

  return (
    <Form
      onSubmit={(e) => {
        e.preventDefault()
        formik.handleSubmit()
      }}
    >
      {pageContent}

      {saveButtonContent}
    </Form>
  )
}

export default OfferSoftFacts
